/**
 * Documentação detalhada da tela Atendimento para Central de Ajuda e assistente.
 * Textos visíveis (summary, body, steps) em linguagem simples; searchText mantém sinônimos técnicos para busca/IA.
 */

export const CHAT_SCREEN_OVERVIEW = {
  title: "Tela Atendimento — visão geral",
  summary:
    "O Atendimento organiza o trabalho em três colunas no computador: lista de solicitações (esquerda), conversa com área de mensagem (centro) e detalhes do cliente/solicitação (direita). No celular, uma coluna por vez. Cada solicitação é uma conversa com histórico, responsável, status e canal de origem.",
  url: "/help?area=chat",
  searchText:
    "atendimento chat conversa solicitação layout colunas lista mensagens detalhes composer /chat",
};

/** Áreas da tela com funcionalidades e regras. */
export const CHAT_AREAS = [
  {
    id: "chat.list.toolbar",
    title: "Lista — barra superior",
    summary:
      "No topo da lista: atualizar solicitações, criar nova solicitação (+) e recolher o painel. O botão + abre Nova solicitação com cliente, tipo, canal, responsável e mensagem inicial opcional.",
    body: [
      "Atualizar: recarrega a lista com os filtros atuais.",
      "Nova solicitação (+): cadastra cliente (existente ou novo), tipo de atendimento, canal de origem, responsável (fila, usuário, chatbot ou agente de IA) e mensagem inicial.",
      "Regra: ao criar solicitação, se já existir outra aberta com o mesmo telefone em Pendente ou Em atendimento, o sistema avisa — evite duplicar para o mesmo cliente.",
    ].join("\n"),
    url: "/help?area=chat",
    searchText: "nova solicitação criar refresh atualizar lista toolbar",
  },
  {
    id: "chat.list.search",
    title: "Lista — busca e Meu dia",
    summary:
      "O campo Buscar solicitações filtra por nome do cliente na lista carregada. O atalho Meu dia aplica de uma vez: suas solicitações pendentes ou em atendimento — foco no que precisa de ação agora.",
    body: [
      "Busca: filtra localmente pelo nome do cliente nas solicitações já carregadas.",
      "Meu dia: atalho para suas solicitações com status Pendente ou Em atendimento (padrão ao entrar, pode ficar salvo na conta da empresa).",
      "Dica: use Meu dia no início do turno; abra Mais filtros para visão ampla da equipe ou fila.",
    ].join("\n"),
    url: "/help?area=chat",
    searchText: "buscar meu dia minhas solicitações pendente em atendimento filtro chip",
  },
  {
    id: "chat.list.filters",
    title: "Lista — filtros (Quem, Status, Outros)",
    summary:
      "Menu de filtros por responsável (suas, de colegas, fila, chatbots, agentes de IA), por status (Pendente, Em atendimento, Contato futuro, Concluída, Perdida, Cancelada) e outros (Pipeline de Vendas, Não lidas, WhatsApp pessoal).",
    body: [
      "Quem: Minhas Solicitações | Outros Usuários | Fila (sem responsável) | Chatbots | Agentes de IA.",
      "Status: Pendente | Em atendimento | Contato futuro | Concluída | Perdida | Cancelada.",
      "Outros: Pipeline de Vendas | Não lidas | WhatsApp pessoal (só se você tiver WhatsApp pessoal conectado).",
      "Regra: solicitações fixadas (por você ou pelo canal) aparecem sempre no topo da lista.",
      "Filtros salvos: combinações favoritas podem ser aplicadas pelo menu.",
    ].join("\n"),
    url: "/help?area=chat",
    searchText: "filtro fila chatbot agente ia status pendente concluída não lidas whatsapp pessoal pipeline",
  },
  {
    id: "chat.list.advanced",
    title: "Lista — busca avançada e tabela",
    summary:
      "Busca avançada filtra por tipo, subtipos, canal, responsável, etiquetas, datas, texto na mensagem e mais. Ver em tabela abre a mesma seleção para operações em massa.",
    body: [
      "Busca avançada: origem/canal, árvore de tipos, responsável, etiquetas, período, quem interagiu, texto na conversa.",
      "Etiquetas removíveis mostram filtros avançados ativos; Limpar avançados remove só esses.",
      "Ver em tabela: exporta a visão filtrada para tela de resultados com ações em lote.",
      "Na ficha do cliente, a aba Solicitações também permite selecionar protocolos para unificar, distribuir, alterar e exportar.",
    ].join("\n"),
    url: "/help?area=chat",
    searchText: "busca avançada tabela search results filtros tags tipo subtipo",
  },
  {
    id: "chat.list.row",
    title: "Lista — linha da solicitação",
    summary:
      "Cada linha mostra cliente, prévia da última mensagem, canal, protocolo (#id), indicador de não lida, relógio do WhatsApp 24h, fixação e seletores rápidos de responsável e status sem abrir a conversa.",
    body: [
      "Indicador azul: mensagens não lidas do cliente.",
      "Relógio WhatsApp: alerta quando a janela de 24h está acabando ou expirou (WhatsApp oficial Meta).",
      "Responsável e status na linha: altere direto na lista.",
      "Menu contexto (botão direito): Fixar no topo / Desafixar; Abrir em nova janela.",
      "Ctrl+clique: abre conversa em janela popup.",
      "Regra fixar: fixação manual (você) ou automática (regra do canal ao entrar). Use Desafixar no menu de contexto para remover qualquer fixação, inclusive a do canal.",
    ].join("\n"),
    url: "/help?area=chat",
    searchText: "linha lista protocolo não lida fixar responsável status inline popup cloud api",
  },
  {
    id: "chat.header",
    title: "Conversa — cabeçalho",
    summary:
      "No topo da conversa: nome do cliente (editável), protocolo, canal, navegação na lista filtrada, seletor \"Responsável\", status, etiquetas, imprimir, popup e painel de detalhes.",
    body: [
      "Navegação na fila: setas ou ↑↓ para ir à solicitação anterior/próxima dentro dos filtros atuais.",
      "Responsável: Fila | colega | chatbot (atendimento automático) | agente de IA — opções mutuamente exclusivas (agente limpa usuário e vice-versa).",
      "Status: alteração imediata; ao sair de Contato futuro, a data de próximo contato é limpa.",
      "Protocolo: número #id da solicitação para referência interna.",
      "Imprimir: gera versão para impressão do histórico.",
      "Detalhes: abre/fecha coluna direita (Cliente, Solicitação, Equipe, etc.).",
    ].join("\n"),
    url: "/help?area=chat",
    searchText: "cabeçalho header responsável status protocolo fila navegação imprimir detalhes transferir chip",
  },
  {
    id: "chat.header.whatsapp",
    title: "Conversa — WhatsApp 24h e voz",
    summary:
      "No WhatsApp oficial (Meta), um ícone de relógio indica o tempo restante da janela de 24h para resposta livre. Fora da janela é obrigatório usar modelo de mensagem de reengajamento. Alguns canais também exibem opção de ligação de voz.",
    body: [
      "Dentro de 24h: mensagens livres, mídia com legenda, modelos opcionais.",
      "Fora de 24h ou primeiro contato iniciado pela empresa: selecione modelo de reengajamento na área de mensagem.",
      "Mídia sem legenda: WhatsApp costuma rejeitar — inclua texto.",
      "WhatsApp pessoal: conversas separadas; só texto na área de mensagem; opção Promover a solicitação formal.",
    ].join("\n"),
    url: "/help?area=chat",
    searchText: "whatsapp 24 horas janela template reengajamento voz gupshup pessoal promover cloud api outbound composer",
  },
  {
    id: "chat.status",
    title: "Status da solicitação — regras",
    summary:
      "Seis status: Pendente, Em atendimento, Contato futuro, Concluída, Perdida, Cancelada. Concluída, Perdida e Cancelada fecham a conversa — a área de mensagem some e aparece opção Reabrir ou Nova solicitação.",
    body: [
      "Pendente: aguardando primeira resposta ou após transferência.",
      "Em atendimento: trabalho ativo.",
      "Contato futuro: agendado; mantém data de próximo contato até mudar status.",
      "Concluída / Perdida / Cancelada: encerram; aviso no lugar da área de mensagem.",
      "Reabrir: volta para Em atendimento.",
      "Regra: automações podem bloquear mudança de status (diálogo explica a regra).",
      "Regra: tarefas obrigatórias pendentes impedem encerrar ou mudar tipo — complete ou remova bloqueio nas Tarefas.",
    ].join("\n"),
    url: "/help?area=chat",
    searchText: "status pendente em atendimento concluída perdida cancelada contato futuro reabrir encerrar composer",
  },
  {
    id: "chat.transfer",
    title: "Transferência e responsável — regras",
    summary:
      "Transferir = alterar responsável: fila, colega, chatbot ou agente de IA. Pode ser pelo seletor \"Responsável\" no topo, na lista, nos Detalhes da solicitação ou digitando @nome na área de mensagem (confirma transferência ao enviar).",
    body: [
      "Fila: sem usuário nem agente — aguarda alguém assumir.",
      "Colega: um usuário da equipe (pessoa humana).",
      "Chatbot: dispara fluxo automático de menus e respostas.",
      "Agente de IA: responde automaticamente até escalonamento ou transferência.",
      "Regra: agente de IA e pessoa humana não ficam ativos juntos como responsável principal.",
      "Menção @ na área de mensagem: mencionar colega, chatbot ou agente pode transferir ao enviar — confirme se a mensagem vai ao cliente ou vira anotação interna.",
      "Regras automáticas e chatbot também podem transferir via ação Alterar solicitação.",
    ].join("\n"),
    url: "/help?area=chat",
    searchText: "transferir responsável fila humano chatbot agente ia menção arroba assignee chip composer ura id_usuario id_agente",
  },
  {
    id: "chat.composer.modes",
    title: "Área de mensagem — modo cliente vs anotação interna",
    summary:
      "As opções Responder cliente / Anotação interna definem se a mensagem vai ao contato ou fica só para a equipe. Anotações internas não exigem telefone do cliente nem modelo de mensagem WhatsApp.",
    body: [
      "Responder cliente: envia pelo canal selecionado (WhatsApp, e-mail, SMS, etc.).",
      "Anotação interna: visível só para equipe; canal Interno.",
      "Banner amarelo avisa quando a mensagem será enviada externamente.",
      "Regra: canal pode exigir telefone cadastrado no cliente — cadastre em Detalhes ou use anotação interna.",
    ].join("\n"),
    url: "/help?area=chat",
    searchText: "composer anotação interna responder cliente modo mensagem equipe",
  },
  {
    id: "chat.composer.tools",
    title: "Área de mensagem — canal, anexos, formatação e IA",
    summary:
      "Seletor de canal agrupa Interno, WhatsApp, E-mail, Telefone/SMS, Redes, Portal. Anexos: arquivo, produtos (#), localização, modelos (WhatsApp). Formatação: negrito, itálico, listas. Sugerir resposta com IA gera rascunho a partir do histórico.",
    body: [
      "Canal: escolha por onde enviar; canais fixados ficam no topo (preferência salva no navegador).",
      "Anexar arquivo: arrastar e soltar também funciona.",
      "Produtos (#): abre catálogo e carrinho vinculado à solicitação.",
      "Modelos WhatsApp: busca de modelos de reengajamento quando necessário.",
      "Sugestão IA: disponível após haver histórico na conversa.",
      "Gravação de áudio: quando o canal suporta.",
      "Rascunho: texto da área de mensagem é salvo automaticamente por conversa.",
    ].join("\n"),
    url: "/help?area=chat",
    searchText: "composer anexo arquivo produto template formatação negrito ia sugerir resposta áudio rascunho",
  },
  {
    id: "chat.timeline",
    title: "Histórico e linha do tempo",
    summary:
      "Linha do tempo com mensagens do cliente, equipe, atividades (transferências, status) e, para administradores da plataforma, registros técnicos do sistema. Filtros: Conversa, Interno, Atividade, Acessos (LGPD), Registros técnicos.",
    body: [
      "Carregar mensagens anteriores: role para cima para ver mensagens mais antigas.",
      "Tipos de mensagem: texto, mídia, produtos, passagens, atividades automáticas etc.",
      "Excluir mensagem: permitido em prazo limitado quando elegível (ícone na mensagem).",
      "Filtros da linha do tempo reduzem ruído em conversas longas.",
      "Acessos (LGPD): camada opcional que mostra quem abriu a solicitação e quais dados do histórico/cadastro estavam visíveis. Recarregar a página ou voltar sem informação nova não gera registro.",
    ].join("\n"),
    url: "/help?area=chat",
    searchText: "histórico timeline mensagens atividade logs api filtro carregar anteriores master lgpd acesso visualizou",
  },
  {
    id: "chat.details.client",
    title: "Detalhes — Cliente",
    summary:
      "Painel Cliente: nome, telefones, e-mail, CPF, endereço, etiquetas, campos personalizados, nível de consciência e link de cadastro externo. Edite dados sem sair do atendimento.",
    body: [
      "Clique no nome no cabeçalho para editar rapidamente.",
      "Telefone duplicado pode sugerir mesclar clientes.",
      "Etiquetas e campos personalizados vêm das configurações da conta da empresa.",
    ].join("\n"),
    url: "/help?area=chat",
    searchText: "detalhes cliente telefone email cpf tags campos personalizados workspace",
  },
  {
    id: "chat.details.request",
    title: "Detalhes — Solicitação",
    summary:
      "Detalhes da solicitação: status, próximo contato, etiquetas, empresa, canal de origem, árvore de tipos/subtipos, responsável, potencial, NF e campos dinâmicos do tipo.",
    body: [
      "Tipo de atendimento: categoriza para relatórios e automações — alterar pode exigir tarefas concluídas.",
      "Canal de origem: de onde entrou a conversa (WhatsApp, e-mail, etc.).",
      "Responsável: mesmo seletor do cabeçalho.",
      "Próximo contato: usado com status Contato futuro.",
    ].join("\n"),
    url: "/help?area=chat",
    searchText: "detalhes solicitação tipo subtipo canal origem potencial próximo contato nf",
  },
  {
    id: "chat.details.collaboration",
    title: "Detalhes — Equipe, Tarefas e IA",
    summary:
      "Equipe: conversa interna entre colegas sobre a solicitação. Tarefas: quadro de pendências da solicitação. Tirar dúvidas: assistente com documentos do tipo de atendimento. Resumo com IA: síntese contextual da conversa.",
    body: [
      "Equipe: mensagens internas separadas do cliente.",
      "Tarefas obrigatórias: impedem encerrar solicitação até concluir.",
      "Tirar dúvidas: usa materiais cadastrados no tipo de atendimento.",
      "Resumo com IA: carrega ao expandir a seção.",
      "Produtos: catálogo e itens vinculados (segmentos não transporte).",
      "Passagens: venda de passagem no transporte rodoviário (quando habilitado).",
      "Histórico: outras solicitações do mesmo cliente.",
    ].join("\n"),
    url: "/help?area=chat",
    searchText: "equipe tarefas insights ia tirar dúvidas produtos passagens bpe histórico colaboração team_chat team_tasks rag kanban",
  },
  {
    id: "chat.automation",
    title: "Automações e bloqueios no atendimento",
    summary:
      "Regras de automação podem impedir transferência, mudança de status ou tipo — o sistema mostra qual regra bloqueou. Automações também podem executar essas ações automaticamente.",
    body: [
      "Diálogo de bloqueio: informa a regra quando uma ação manual não é permitida.",
      "Validação antes de alterar: o sistema confere regras antes de mudar status, tipo ou responsável.",
      "Tarefas obrigatórias: outro bloqueio comum ao encerrar.",
    ].join("\n"),
    url: "/help?section=settings&area=automation_rules",
    searchText: "automação bloqueio regra status transferir tarefa obrigatória before-change guard",
  },
  {
    id: "chat.permissions",
    title: "Permissões no Atendimento",
    summary:
      "Quem não tem permissão de atendimento não abre a tela. Venda de passagem, chat da equipe e tarefas dependem de permissões específicas do cargo. Registros técnicos do histórico só aparecem para administradores da plataforma.",
    body: [
      "Sem permissão de atendimento: a tela Atendimento não abre.",
      "Envio, transferência e status: em geral liberados para quem acessa o atendimento.",
      "Se a conta da empresa não tem canais conectados, um aviso orienta o administrador a configurar em Configurações → Canais.",
    ].join("\n"),
    url: "/help?area=chat",
    searchText: "permissão chat.view bpe team_chat team_tasks acesso master logs api",
  },
];

/** Fluxos comuns dentro do Atendimento. */
export const CHAT_WORKFLOWS = [
  {
    id: "workflow.chat.assume_and_reply",
    title: "Assumir solicitação da fila e responder",
    summary: "Pegar conversa da fila, colocar Em atendimento e enviar resposta ao cliente.",
    steps: [
      "Filtre Fila ou use busca.",
      "Abra a solicitação.",
      "Em Responsável, selecione você ou Minha solicitação.",
      "Mude status para Em atendimento se ainda estiver Pendente.",
      "Escreva em Responder cliente, escolha canal e envie.",
    ],
    url: "/help?area=chat",
    searchText: "assumir fila responder minhas solicitações pegar conversa",
  },
  {
    id: "workflow.chat.transfer_to_agent",
    title: "Transferir conversa para agente de IA",
    summary: "Encaminhar solicitação em andamento para um agente de IA responder automaticamente.",
    steps: [
      "Abra a conversa.",
      "No seletor \"Responsável\", escolha Agentes de IA e selecione o agente.",
      "Status pode permanecer Em atendimento ou Pendente conforme processo.",
      "O agente passa a processar novas mensagens do cliente.",
    ],
    url: "/help?area=chat",
    searchText: "transferir agente ia responsável automático chip",
  },
  {
    id: "workflow.chat.close_with_tasks",
    title: "Encerrar solicitação com tarefas pendentes",
    summary: "Sistema pode bloquear Concluída/Perdida/Cancelada se houver tarefas obrigatórias abertas.",
    steps: [
      "Abra a seção Tarefas no painel direito.",
      "Conclua ou reatribua tarefas obrigatórias.",
      "Altere status para Concluída (ou Perdida/Cancelada).",
      "Se automação bloquear, leia a mensagem e ajuste conforme a regra.",
    ],
    url: "/help?area=chat",
    searchText: "encerrar concluir tarefa obrigatória bloqueio status",
  },
  {
    id: "workflow.chat.whatsapp_template",
    title: "Responder WhatsApp fora da janela de 24h",
    summary: "Quando a janela expirou, use modelo de mensagem de reengajamento antes de enviar.",
    steps: [
      "Verifique ícone de relógio no topo da conversa.",
      "Na área de mensagem, busque e selecione um modelo de reengajamento.",
      "Preencha variáveis se o modelo exigir.",
      "Envie; após resposta do cliente, volta a janela livre de 24h.",
    ],
    url: "/help?area=chat",
    searchText: "whatsapp template 24h reengajamento janela expirada composer cloud api",
  },
];
